import { useEffect } from 'react';
import {
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { Button } from '@mercator-gcp/grout';
import { useForm } from 'react-hook-form';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { useCreateHubspotContact } from 'hooks/useHubspotContacts';
import { useToast } from 'hooks/useToast';
import { HubspotContactInput } from 'services/API/OneAuthAPI';

type AddHubspotContactProps = {
  isOpen: boolean;
  onClose: () => void;
  identity?: string;
};

const FIELDS: { name: keyof HubspotContactInput; label: string; type?: string }[] = [
  { name: 'firstName', label: 'First name' },
  { name: 'lastName', label: 'Last name' },
  { name: 'email', label: 'Email', type: 'email' },
  { name: 'phone', label: 'Phone' },
  { name: 'company', label: 'Company' },
  { name: 'jobTitle', label: 'Job title' },
];

export const AddHubspotContact = ({
  isOpen,
  onClose,
  identity,
}: AddHubspotContactProps) => {
  const { register, handleSubmit, reset, watch } = useForm<HubspotContactInput>();
  const { renderCustomToast: toast } = useToast();
  const createContact = useCreateHubspotContact(identity);

  // Require at least one non-empty field (HubSpot rejects an empty contact).
  const values = watch();
  const hasAnyValue = Object.values(values).some((value) => value?.trim());

  const onSubmit = (data: HubspotContactInput) => {
    createContact.mutate(data);
  };

  useEffect(() => {
    if (createContact.isSuccess) {
      toast({ title: 'Contact created', status: 'success' });
      reset();
      onClose();
    }
  }, [createContact.isSuccess]);

  useEffect(() => {
    if (createContact.isError) {
      toast({ title: 'Failed to create contact', status: 'error' });
    }
  }, [createContact.isError]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add HubSpot contact</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <FlexColumn sx={{ gap: 'padding_12' }}>
              {FIELDS.map((field) => (
                <FormControl key={field.name}>
                  <FormLabel>{field.label}</FormLabel>
                  <Input type={field.type ?? 'text'} {...register(field.name)} />
                </FormControl>
              ))}
            </FlexColumn>
          </ModalBody>
          <ModalFooter sx={{ gap: 'padding_8' }}>
            <Button variant="functional" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              isLoading={createContact.isLoading}
              isDisabled={!hasAnyValue || createContact.isLoading}
            >
              Create contact
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
